import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { authFetch } from '../../utils';
import { batteryPercentage } from '../../helpers/battery';
import { linkQuality, qualityColor } from '../../helpers/linkQuality';

const HOURS = 24;

function batteryColor(pct) {
  if (pct == null) return '#999';
  return pct > 40 ? '#16a34a' : pct > 20 ? '#d97706' : '#dc2626';
}

function formatDate(ts) {
  return new Date(ts).toLocaleString('es-ES');
}

const thStyle = { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '13px', color: '#495057' };
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };
const cardStyle = { backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' };

export default function DataHealthTab() {
  const [detectors, setDetectors] = useState([]);
  const [allGateways, setAllGateways] = useState([]);
  const [gatewayLinks, setGatewayLinks] = useState([]);
  const [selectedDetectorId, setSelectedDetectorId] = useState('');
  const [observations, setObservations] = useState([]);
  const [loadingObservations, setLoadingObservations] = useState(false);

  useEffect(() => {
    authFetch(`${API_URL}smoke-detectors`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setDetectors(Array.isArray(data) ? data : []))
      .catch(() => setDetectors([]));

    authFetch(`${API_URL}gateways/links?hours=${HOURS}`)
      .then(res => res.ok ? res.json() : { gateways: [], links: [] })
      .then(data => {
        setAllGateways(Array.isArray(data.gateways) ? data.gateways : []);
        setGatewayLinks(Array.isArray(data.links) ? data.links : []);
      })
      .catch(() => { setAllGateways([]); setGatewayLinks([]); });
  }, []);

  useEffect(() => {
    if (!selectedDetectorId) {
      setObservations([]);
      return;
    }

    setLoadingObservations(true);
    const end = new Date();
    const start = new Date(end.getTime() - HOURS * 60 * 60 * 1000);

    authFetch(`${API_URL}smoke-detectors/${selectedDetectorId}/observations?start=${start.toISOString()}&end=${end.toISOString()}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setObservations(Array.isArray(data) ? data : []))
      .catch(() => setObservations([]))
      .finally(() => setLoadingObservations(false));
  }, [selectedDetectorId]);

  const selectedDetector = detectors.find(d => d.detectorId === selectedDetectorId);
  const selectedLink = gatewayLinks.find(l => l.detectorId === selectedDetectorId);
  const gateways = selectedLink?.gateways ?? [];

  const batteryPct = selectedDetector?.lastObservation?.batteryVoltage != null
    ? batteryPercentage(selectedDetector.lastObservation.batteryVoltage)
    : null;

  const sortedObservations = [...observations].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1rem' }}>
      <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem', textAlign: 'center' }}>
        📊 Salud Detectores
      </h2>

      <label style={{ display: 'block', marginBottom: '1.5rem', fontSize: '14px' }}>
        Detector:&nbsp;
        <select
          value={selectedDetectorId}
          onChange={e => setSelectedDetectorId(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', minWidth: '220px' }}
        >
          <option value="">Selecciona un detector</option>
          {detectors.map(d => (
            <option key={d.detectorId} value={d.detectorId}>{d.detectorId}</option>
          ))}
        </select>
      </label>

      {selectedDetectorId && (
        <>
          <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Salud del detector ({HOURS}h)</h3>

            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Datos recibidos</div>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{observations.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Gateways</div>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{gateways.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Batería</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: batteryColor(batteryPct) }}>
                  {batteryPct != null ? `${batteryPct}%` : '—'}
                </div>
              </div>
            </div>

            {gateways.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '13px' }}>Calidad de enlace por gateway</div>
                {gateways.map((gw, i) => {
                  const q = linkQuality(gw.avgSnr, gw.avgDr);
                  const gatewayName = allGateways.find(g => g.gatewayId === gw.gatewayId)?.name ?? gw.gatewayId;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', fontSize: '13px' }}>
                      <span style={{ flex: 1 }}>{gatewayName}</span>
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        SNR {gw.avgSnr != null ? gw.avgSnr.toFixed(1) : '—'} dB · RSSI {gw.avgRssi != null ? Math.round(gw.avgRssi) : '—'} dBm · {gw.count} envíos
                      </span>
                      {q != null && (
                        <span style={{ fontWeight: 700, color: qualityColor(q), minWidth: '32px', textAlign: 'right' }}>{q}%</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <h3 style={{ marginBottom: '0.5rem' }}>Datos recibidos ({HOURS}h)</h3>
          {loadingObservations ? (
            <p style={{ color: '#6c757d' }}>Cargando datos...</p>
          ) : sortedObservations.length === 0 ? (
            <p style={{ color: '#6c757d' }}>Sin datos recibidos en las últimas {HOURS} horas.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>Temperatura</th>
                    <th style={thStyle}>Humedad</th>
                    <th style={thStyle}>Batería</th>
                    <th style={thStyle}>Alarma</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedObservations.map(o => (
                    <tr key={o._id}>
                      <td style={tdStyle}>{formatDate(o.timestamp)}</td>
                      <td style={tdStyle}>{o.temperature != null ? `${o.temperature} °C` : '—'}</td>
                      <td style={tdStyle}>{o.humidity != null ? `${o.humidity} %` : '—'}</td>
                      <td style={tdStyle}>{o.batteryVoltage != null ? `${batteryPercentage(o.batteryVoltage)} %` : '—'}</td>
                      <td style={tdStyle}>{o.alarm ? '🔴' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
